import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { isOfferableCatalogJob, retiredCatalogSuffix } from '@/lib/catalog/newJobCatalogQuery';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { AddonRuleQualityPanel } from '@/components/tickets/AddonRuleQualityPanel';
import { VehicleApplicabilityExceptions } from '@/components/tickets/VehicleApplicabilityExceptions';
import { parseVehicleExceptions } from '@/utils/addonVehicleApplicability';
import type { VehicleApplicabilityException } from '@/types/addonSuggestions';

interface CatalogJob {
  id: string;
  label: string;
  category_key: string;
  sub_menu: string;
  retired_at?: string | null;
  superseded_by_code?: string | null;
}

export default function AddonSuggestionSettings() {
  const navigate = useNavigate();
  const { activeShop } = useAuth();  // H3: Use AuthContext instead of localStorage
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalogJobs, setCatalogJobs] = useState<CatalogJob[]>([]);
  
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [precisionFirstMode, setPrecisionFirstMode] = useState(true);
  const [adasYearThreshold, setAdasYearThreshold] = useState(2016);
  const [alignmentDefaultChecked, setAlignmentDefaultChecked] = useState(true);
  const [sasDefaultChecked, setSasDefaultChecked] = useState(true);
  const [adasDefaultChecked, setAdasDefaultChecked] = useState(false);
  const [tpmsDefaultChecked, setTpmsDefaultChecked] = useState(false);
  const [alwaysPromptTerms, setAlwaysPromptTerms] = useState('');
  const [neverPromptTerms, setNeverPromptTerms] = useState('');
  const [alignmentJobId, setAlignmentJobId] = useState<string>('');
  const [sasJobId, setSasJobId] = useState<string>('');
  const [adasJobId, setAdasJobId] = useState<string>('');
  const [tpmsJobId, setTpmsJobId] = useState<string>('');
  // W2: live rows, plus a retired row only while it is the configured value.
  const configuredJobIds = [alignmentJobId, sasJobId, adasJobId, tpmsJobId];
  const visibleCatalogJobs = catalogJobs.filter(
    (job) => isOfferableCatalogJob(job) || configuredJobIds.includes(job.id),
  );
  // CORE-BP item 2 — the shop's own vehicle applicability exceptions.
  const [vehicleExceptions, setVehicleExceptions] =
    useState<VehicleApplicabilityException[]>([]);

  useEffect(() => {
    // H3: Guard - require active shop from auth context
    if (!activeShop?.id) {
      toast({ title: 'No active shop', variant: 'destructive' });
      navigate('/');
      return;
    }
    loadData();
  }, [activeShop?.id]);

  async function loadData() {
    try {
      // H3: Use activeShop from context (validated by AuthContext)
      const shopId = activeShop?.id;
      if (!shopId) {
        toast({ title: 'No active shop', variant: 'destructive' });
        navigate('/');
        return;
      }

      // Load catalog jobs (config picker). Retired rows are loaded so a setting that
      // already points at one still renders (badged); they are never offered otherwise —
      // see visibleCatalogJobs below (W2 catalog retirement).
      const { data: jobs } = await supabase
        .from('jobs_catalog')
        .select('id, label, category_key, sub_menu, retired_at, superseded_by_code')
        .order('category_key', { ascending: true })
        .order('sub_menu', { ascending: true })
        .order('label', { ascending: true });

      if (jobs) {
        setCatalogJobs(jobs);
      }

      // Load settings or create defaults
      const { data: settings, error } = await supabase
        .from('addon_suggestion_settings' as any)
        .select('*')
        .eq('shop_id', shopId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (settings) {
        setFeatureEnabled((settings as any).feature_enabled);
        setPrecisionFirstMode((settings as any).precision_first_mode);
        setAdasYearThreshold((settings as any).adas_year_threshold);
        setAlignmentDefaultChecked((settings as any).alignment_default_checked);
        setSasDefaultChecked((settings as any).sas_default_checked);
        setAdasDefaultChecked((settings as any).adas_default_checked);
        setTpmsDefaultChecked((settings as any).tpms_default_checked);
        setAlwaysPromptTerms(((settings as any).always_prompt_terms || []).join(', '));
        setNeverPromptTerms(((settings as any).never_prompt_terms || []).join(', '));
        setAlignmentJobId((settings as any).alignment_catalog_job_id || '');
        setSasJobId((settings as any).sas_catalog_job_id || '');
        setAdasJobId((settings as any).adas_catalog_job_id || '');
        setTpmsJobId((settings as any).tpms_catalog_job_id || '');
        setVehicleExceptions(parseVehicleExceptions((settings as any).vehicle_exceptions));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({ title: 'Failed to load settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      // H3: Use activeShop from context (validated by AuthContext)
      const shopId = activeShop?.id;
      if (!shopId) throw new Error('No active shop');

      const settingsData = {
        shop_id: shopId,
        feature_enabled: featureEnabled,
        precision_first_mode: precisionFirstMode,
        adas_year_threshold: adasYearThreshold,
        alignment_default_checked: alignmentDefaultChecked,
        sas_default_checked: sasDefaultChecked,
        adas_default_checked: adasDefaultChecked,
        tpms_default_checked: tpmsDefaultChecked,
        always_prompt_terms: alwaysPromptTerms.split(',').map(t => t.trim()).filter(Boolean),
        never_prompt_terms: neverPromptTerms.split(',').map(t => t.trim()).filter(Boolean),
        alignment_catalog_job_id: alignmentJobId || null,
        sas_catalog_job_id: sasJobId || null,
        adas_catalog_job_id: adasJobId || null,
        tpms_catalog_job_id: tpmsJobId || null,
        // Written as the shop authored it; the evaluator normalises on read.
        vehicle_exceptions: vehicleExceptions,
      };

      const { error } = await supabase
        .from('addon_suggestion_settings' as any)
        .upsert(settingsData, { onConflict: 'shop_id' });

      if (error) throw error;

      toast({ title: 'Settings saved successfully' });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({ title: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl font-bold">Add-On Suggestion Settings</h1>
        <p className="text-muted-foreground mt-2">
          Configure automatic suggestions for alignment, ADAS, SAS, and TPMS services
        </p>
      </div>

      <div className="space-y-6">
        {/*
          CORE-BP item 5. Placed FIRST deliberately: the question a manager
          opens this page to answer is whether the current rules are landing,
          and that should be visible before the controls that change them.
          Reads the person-free aggregate only — see AddonRuleQualityPanel.
        */}
        <AddonRuleQualityPanel shopId={activeShop?.id} />

        {/* CORE-BP item 2. Placed with the other shop-authored policy, because
            that is what it is — not a data feed. */}
        <VehicleApplicabilityExceptions
          value={vehicleExceptions}
          onChange={setVehicleExceptions}
        />

        <Card>
          <CardHeader>
            <CardTitle>Feature Control</CardTitle>
            <CardDescription>Enable or disable the add-on suggestion system</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="feature-enabled">Enable add-on suggestions</Label>
                <p className="text-sm text-muted-foreground">
                  Show suggestion modal before creating tickets
                </p>
              </div>
              <Switch
                id="feature-enabled"
                checked={featureEnabled}
                onCheckedChange={setFeatureEnabled}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="precision-mode">Precision-first mode</Label>
                <p className="text-sm text-muted-foreground">
                  Conservative suggestions with higher accuracy
                </p>
              </div>
              <Switch
                id="precision-mode"
                checked={precisionFirstMode}
                onCheckedChange={setPrecisionFirstMode}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ADAS Configuration</CardTitle>
            <CardDescription>Set the minimum vehicle year for ADAS suggestions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="adas-year">Minimum vehicle year</Label>
              <Input
                id="adas-year"
                type="number"
                value={adasYearThreshold}
                onChange={(e) => setAdasYearThreshold(parseInt(e.target.value) || 2016)}
                min="2000"
                max="2030"
                className="max-w-xs"
              />
              <p className="text-sm text-muted-foreground">
                ADAS suggestions only appear for vehicles newer than this year
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Default Checked States</CardTitle>
            <CardDescription>Control which suggestions are pre-selected by default</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="alignment-checked">Alignment default checked</Label>
              <Switch
                id="alignment-checked"
                checked={alignmentDefaultChecked}
                onCheckedChange={setAlignmentDefaultChecked}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="sas-checked">SAS reset default checked</Label>
              <Switch
                id="sas-checked"
                checked={sasDefaultChecked}
                onCheckedChange={setSasDefaultChecked}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="adas-checked">ADAS calibration default checked</Label>
              <Switch
                id="adas-checked"
                checked={adasDefaultChecked}
                onCheckedChange={setAdasDefaultChecked}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="tpms-checked">TPMS relearn default checked</Label>
              <Switch
                id="tpms-checked"
                checked={tpmsDefaultChecked}
                onCheckedChange={setTpmsDefaultChecked}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Keyword Overrides</CardTitle>
            <CardDescription>Customize when suggestions appear</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="always-prompt">Always prompt terms (comma-separated)</Label>
              <Textarea
                id="always-prompt"
                value={alwaysPromptTerms}
                onChange={(e) => setAlwaysPromptTerms(e.target.value)}
                placeholder="cam bolt, custom job name, etc."
                rows={3}
              />
              <p className="text-sm text-muted-foreground">
                Jobs containing these terms will always trigger suggestions
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="never-prompt">Never prompt terms (comma-separated)</Label>
              <Textarea
                id="never-prompt"
                value={neverPromptTerms}
                onChange={(e) => setNeverPromptTerms(e.target.value)}
                placeholder="oil change, inspection, etc."
                rows={3}
              />
              <p className="text-sm text-muted-foreground">
                Jobs containing these terms will never trigger suggestions
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Catalog Job Mapping</CardTitle>
            <CardDescription>Select which catalog jobs to add for each suggestion type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="alignment-job">Alignment job</Label>
              <Select value={alignmentJobId} onValueChange={setAlignmentJobId}>
                <SelectTrigger id="alignment-job">
                  <SelectValue placeholder="Select catalog job" />
                </SelectTrigger>
                <SelectContent>
                  {visibleCatalogJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.category_key} → {job.sub_menu} → {job.label}{retiredCatalogSuffix(job)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sas-job">SAS reset job</Label>
              <Select value={sasJobId} onValueChange={setSasJobId}>
                <SelectTrigger id="sas-job">
                  <SelectValue placeholder="Select catalog job" />
                </SelectTrigger>
                <SelectContent>
                  {visibleCatalogJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.category_key} → {job.sub_menu} → {job.label}{retiredCatalogSuffix(job)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adas-job">ADAS calibration job</Label>
              <Select value={adasJobId} onValueChange={setAdasJobId}>
                <SelectTrigger id="adas-job">
                  <SelectValue placeholder="Select catalog job" />
                </SelectTrigger>
                <SelectContent>
                  {visibleCatalogJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.category_key} → {job.sub_menu} → {job.label}{retiredCatalogSuffix(job)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tpms-job">TPMS relearn job</Label>
              <Select value={tpmsJobId} onValueChange={setTpmsJobId}>
                <SelectTrigger id="tpms-job">
                  <SelectValue placeholder="Select catalog job" />
                </SelectTrigger>
                <SelectContent>
                  {visibleCatalogJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.category_key} → {job.sub_menu} → {job.label}{retiredCatalogSuffix(job)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Separator />

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}
